import { useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { toSupportedLng } from "@heraion/i18n";
import {
  THEME_FAMILIES,
  THEME_MODES,
  type GlucoseUnitsPreference,
  type ThemeFamily,
  type ThemeMode,
  type UnitsPreference,
} from "@heraion/shared";
import {
  PreauthLanguagePicker,
  SettingsPillChoice,
  SettingsSelectChoice,
  WeekStartChoice,
  resolveTheme,
  useThemePreferenceContext,
  type UiTheme,
} from "@heraion/ui";
import { useThemePalette } from "./theme";
import { useProfileDoc } from "./useProfileDoc";

// Settings → Preferences on the web console: default weight unit + app
// language. Mirrors mobile's Preferences sub-page (units + language; the
// logger inline-hints toggle is mobile-only and omitted here). Units is a
// profile scalar (get-merge-PUT via useProfileDoc); language drives
// react-i18next directly and the detector persists it to localStorage.

const THEME_MODE_LABELS: Record<ThemeMode, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

export function PreferencesScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const { doc, loading, error, saveField } = useProfileDoc();
  const themeCtx = useThemePreferenceContext();
  const [savingField, setSavingField] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const styles = makeStyles(theme);

  // ⛔ `?? null`, NON `displayUnitOf(doc)`, e non e' una copia dimenticata: qui
  // la domanda e' «l'atleta ha scelto?», non «cosa gli mostro». Questo schermo
  // e' dove la scelta si FA, e un default a "kg" renderebbe il chip kg gia'
  // selezionato per chi non ha mai scelto — cioe' mentirebbe sullo stato.
  // Ogni schermo che invece MOSTRA un peso usa displayUnitOf.
  const units = (doc?.["unitsPreference"] as UnitsPreference | undefined) ?? null;
  const glucoseUnit =
    (doc?.["glucoseUnit"] as GlucoseUnitsPreference | undefined) ?? null;
  const activeLng = toSupportedLng(i18n.language);
  const preference = themeCtx?.preference;

  const save = async (field: string, value: unknown) => {
    setSavingField(field);
    setSaveError(null);
    try {
      await saveField(field, value);
    } catch {
      setSaveError(`api_error_save`);
    } finally {
      setSavingField(null);
    }
  };

  const pickTheme = (field: "family" | "mode", value: ThemeFamily | ThemeMode) => {
    if (!preference) return;
    const next = { ...preference, [field]: value };
    themeCtx?.setPreference(next);
    void save("themePreference", next);
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <SettingsPillChoice<UnitsPreference>
        theme={theme}
        label={t("mobile.prefs.unitsLabel", { defaultValue: "Units" })}
        help={t("mobile.prefs.unitsHelp", {
          defaultValue:
            "Default weight unit for new sets. You can still override per row in the logger.",
        })}
        options={[
          { value: "kg", label: t("mobile.prefs.unitsKg", { defaultValue: "Kilograms (kg)" }) },
          { value: "lb", label: t("mobile.prefs.unitsLb", { defaultValue: "Pounds (lb)" }) },
        ]}
        value={units}
        onPick={(u) => void save("unitsPreference", u)}
        saving={savingField === "unitsPreference"}
        error={savingField === "unitsPreference" ? saveError : error}
      />

      <SettingsPillChoice<GlucoseUnitsPreference>
        theme={theme}
        label={t("mobile.prefs.glucoseUnitsLabel", { defaultValue: "Glucose units" })}
        help={t("mobile.prefs.glucoseUnitsHelp", {
          defaultValue: "How glucose is shown and entered. Stored the same either way.",
        })}
        options={[
          { value: "mmol", label: t("mobile.prefs.glucoseMmol", { defaultValue: "mmol/L" }) },
          { value: "mgdl", label: t("mobile.prefs.glucoseMgdl", { defaultValue: "mg/dL" }) },
        ]}
        value={glucoseUnit}
        onPick={(u) => void save("glucoseUnit", u)}
        saving={savingField === "glucoseUnit"}
        error={savingField === "glucoseUnit" ? saveError : null}
      />

      <WeekStartChoice
        theme={theme}
        value={(doc?.["weekStartDay"] as number | undefined) ?? 1}
        onPick={(d) => void save("weekStartDay", d)}
        locale={i18n.language}
        label={t("mobile.prefs.weekStartLabel", { defaultValue: "Week starts on" })}
        help={t("mobile.prefs.weekStartHelp", {
          defaultValue:
            "Which day your training week resets. Weekly muscle targets count from this day.",
        })}
        saving={savingField === "weekStartDay"}
        error={savingField === "weekStartDay" ? saveError : null}
      />

      <SettingsSelectChoice<ThemeFamily>
        theme={theme}
        label={t("mobile.prefs.themePaletteLabel", { defaultValue: "Theme palette" })}
        help={t("mobile.prefs.themePaletteHelp", {
          defaultValue: "Pick the colour palette used across the app.",
        })}
        options={THEME_FAMILIES.map(({ key, label }) => ({ value: key, label }))}
        value={preference?.family ?? null}
        onPick={(v) => pickTheme("family", v)}
        saving={savingField === "themePreference"}
        error={savingField === "themePreference" ? saveError : null}
      />

      <SettingsPillChoice<ThemeMode>
        theme={theme}
        label={t("mobile.prefs.themeModeLabel", { defaultValue: "Appearance" })}
        help={t("mobile.prefs.themeModeHelp", {
          defaultValue: "Follow the system setting, or force light or dark.",
        })}
        options={THEME_MODES.map((mode) => ({
          value: mode,
          label: t(`mobile.prefs.themeMode.${mode}`, { defaultValue: THEME_MODE_LABELS[mode] }),
        }))}
        value={preference?.mode ?? null}
        onPick={(v) => pickTheme("mode", v)}
        saving={savingField === "themePreference"}
        error={savingField === "themePreference" ? saveError : null}
      />

      <PreauthLanguagePicker
        theme={theme}
        value={activeLng}
        onPick={(lng) => void i18n.changeLanguage(lng)}
      />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s3 },
    center: { flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: p.bg },
  });
}
